import { useState, useEffect, useCallback } from "react";
import * as api from "../api/client";
import { Card, Btn, SectionTitle, ErrorBanner, SuccessBanner, Spinner } from "../components/UI";

const DEFAULT_COLUMNS = {
  analysis: {
    service_name: { label:"Service name",    locked:true,  default:true  },
    planned_cost: { label:"Planned cost",    locked:false, default:true  },
    actual_cost:  { label:"Actual OCI cost", locked:false, default:true  },
    cost_delta:   { label:"Service delta",   locked:false, default:true  },
    net_position: { label:"Net position",    locked:false, default:true  },
    rfp_price:    { label:"RFP unit price",  locked:false, default:false },
    planned_qty:  { label:"Planned qty",     locked:false, default:false },
    actual_qty:   { label:"Actual qty",      locked:false, default:false },
    overage_rfp:  { label:"Overage @ RFP",   locked:false, default:false },
  },
  kpi: {
    planned:       { label:"Planned (RFP)",        default:true  },
    actual:        { label:"Actual OCI cost",       default:true  },
    services_over: { label:"Services over budget",  default:true  },
    net_position:  { label:"Net position",          default:true  },
    months:        { label:"Months analysed",       default:false },
  },
  trend: {
    month:         { label:"Month",         default:true  },
    planned:       { label:"Planned",       default:true  },
    actual:        { label:"Actual OCI",    default:true  },
    net_position:  { label:"Net position",  default:true  },
    services_over: { label:"Services over", default:false },
    status:        { label:"Status badge",  default:false },
  },
};

const initColumns = () => {
  const c = {};
  Object.entries(DEFAULT_COLUMNS).forEach(([sec, cols]) => {
    c[sec] = {};
    Object.entries(cols).forEach(([key, cfg]) => { c[sec][key] = cfg.default; });
  });
  return c;
};

const inp  = { width:"100%", boxSizing:"border-box", fontSize:"13px", padding:"8px 10px", border:"1px solid #E5E7EB", borderRadius:8, background:"var(--color-background-primary)", color:"var(--color-text-primary)", fontFamily:"'DM Sans',sans-serif" };
const mono = { ...inp, fontFamily:"monospace", fontSize:"12px" };
const lbl  = { fontSize:12, fontWeight:600, color:"#6B7280", marginBottom:4, display:"block" };

export default function ClientSettings({ clientId, clientName }) {
  const [tab, setTab]           = useState("oci");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [connStatus, setConn]   = useState(null);
  const [oci, setOci]           = useState({ profile:"", tenancy_ocid:"", user_ocid:"", fingerprint:"", region:"ap-mumbai-1", private_key:"" });
  const [alerts, setAlerts]     = useState({ topic_ocid:"", emails:"", threshold:90, frequency:"daily" });
  const [columns, setColumns]   = useState(initColumns());
  const [contract, setContract] = useState({ start:"", end:"", display_name:"" });

  const ok = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.admin.getSettings(clientId);
      if (r.oci      && Object.keys(r.oci).length)      setOci(p => ({...p, ...r.oci, private_key:""}));
      if (r.alerts   && Object.keys(r.alerts).length)   setAlerts(p => ({...p, ...r.alerts}));
      if (r.contract && Object.keys(r.contract).length) setContract(p => ({...p, ...r.contract}));
      if (r.columns) {
        const parsed = typeof r.columns === "string" ? JSON.parse(r.columns) : r.columns;
        if (Object.keys(parsed).length > 0) setColumns(parsed);
      }
      if (r.oci?.connected) setConn({ status:"connected", tenancy_name: r.oci.tenancy_name });
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const saveOCI = async () => {
    setSaving(true); setError("");
    try { await api.admin.saveOCI(clientId, oci); ok("OCI credentials saved"); load(); }
    catch(e) { setError(e.message); } finally { setSaving(false); }
  };

  const testOCI = async () => {
    setTesting(true); setError(""); setConn(null);
    try {
      const r = await api.admin.testOCI(clientId);
      setConn(r); ok(`Connected — ${r.tenancy_name}`);
    } catch(e) {
      setConn({ status:"failed", error: e.message }); setError(e.message);
    } finally { setTesting(false); }
  };

  const TABS = [
    { id:"oci",      label:"OCI Credentials"        },
    { id:"alerts",   label:"Alerts & Notifications"  },
    { id:"columns",  label:"Dashboard Columns"       },
    { id:"contract", label:"Contract Details"        },
  ];

  if (loading) return <div style={{textAlign:"center",padding:48}}><Spinner size={28}/></div>;

  return (
    <div>
      <SectionTitle children="Client settings" sub={`One-time configuration for ${clientName} — admin only`}/>
      <ErrorBanner message={error} onDismiss={() => setError("")}/>
      {success && <SuccessBanner message={success}/>}

      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"2px solid #F3F4F6" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:"8px 16px", border:"none", cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600,
            background:"transparent",
            color: tab===t.id ? "#2563EB" : "#9CA3AF",
            borderBottom: tab===t.id ? "2px solid #2563EB" : "2px solid transparent",
            marginBottom:-2,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "oci" && (
        <Card>
          <div style={{fontSize:13,color:"#6B7280",marginBottom:16}}>Credentials are encrypted before storage. Never stored as plain text.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div><label style={lbl}>Profile name</label>
              <input style={inp} value={oci.profile} onChange={e => setOci(p=>({...p,profile:e.target.value}))} placeholder="e.g. SBISG"/></div>
            <div><label style={lbl}>Region</label>
              <select style={inp} value={oci.region} onChange={e => setOci(p=>({...p,region:e.target.value}))}>
                <option value="ap-mumbai-1">ap-mumbai-1 (Mumbai)</option>
                <option value="ap-hyderabad-1">ap-hyderabad-1 (Hyderabad)</option>
                <option value="ap-singapore-1">ap-singapore-1 (Singapore)</option>
                <option value="eu-frankfurt-1">eu-frankfurt-1 (Frankfurt)</option>
                <option value="us-ashburn-1">us-ashburn-1 (Ashburn)</option>
              </select></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Tenancy OCID</label>
              <input style={mono} value={oci.tenancy_ocid} onChange={e => setOci(p=>({...p,tenancy_ocid:e.target.value}))} placeholder="ocid1.tenancy.oc1..aaaaaaaXXXX"/></div>
            <div><label style={lbl}>User OCID</label>
              <input style={mono} value={oci.user_ocid} onChange={e => setOci(p=>({...p,user_ocid:e.target.value}))} placeholder="ocid1.user.oc1..aaaaaaaXXXX"/></div>
            <div><label style={lbl}>Fingerprint</label>
              <input style={mono} value={oci.fingerprint} onChange={e => setOci(p=>({...p,fingerprint:e.target.value}))} placeholder="ab:cd:ef:12:34:56"/></div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Private key (.pem){oci.connected && <span style={{marginLeft:8,color:"#16A34A",fontWeight:400}}>✓ Key on file — paste to replace</span>}</label>
              <textarea rows={5} style={{...mono,resize:"vertical"}} value={oci.private_key}
                onChange={e => setOci(p=>({...p,private_key:e.target.value}))}
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;paste key here&#10;-----END RSA PRIVATE KEY-----"/></div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <Btn onClick={saveOCI} loading={saving}>Save credentials</Btn>
            <Btn onClick={testOCI} loading={testing} variant="secondary">Test connection</Btn>
            {connStatus && (
              <span style={{fontSize:13,fontWeight:600,color:connStatus.status==="connected"?"#16A34A":"#DC2626"}}>
                {connStatus.status==="connected" ? `✓ Connected — ${connStatus.tenancy_name}` : `✗ Failed — ${connStatus.error}`}
              </span>
            )}
          </div>
        </Card>
      )}

      {tab === "alerts" && (
        <Card>
          <div style={{fontSize:13,color:"#6B7280",marginBottom:16}}>Configure OCI Notifications for cost threshold alerts.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>OCI Notifications topic OCID</label>
              <input style={mono} value={alerts.topic_ocid} onChange={e => setAlerts(p=>({...p,topic_ocid:e.target.value}))} placeholder="ocid1.onstopic.oc1.ap-mumbai-1.XXXX"/>
              <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>OCI Console → Application Integration → Notifications → Topics</div></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Alert recipient emails (comma separated)</label>
              <input style={inp} value={alerts.emails} onChange={e => setAlerts(p=>({...p,emails:e.target.value}))} placeholder="admin@company.com, ops@company.com"/></div>
            <div><label style={lbl}>Alert threshold</label>
              <select style={inp} value={alerts.threshold} onChange={e => setAlerts(p=>({...p,threshold:Number(e.target.value)}))}>
                <option value={80}>80% of monthly budget</option>
                <option value={90}>90% of monthly budget</option>
                <option value={100}>100% — when over budget</option>
              </select></div>
            <div><label style={lbl}>Alert frequency</label>
              <select style={inp} value={alerts.frequency} onChange={e => setAlerts(p=>({...p,frequency:e.target.value}))}>
                <option value="daily">Once per day (deduplicated)</option>
                <option value="always">Every time threshold crossed</option>
              </select></div>
          </div>
          <Btn onClick={async () => { setSaving(true); try { await api.admin.saveAlerts(clientId, alerts); ok("Alert config saved"); } catch(e){setError(e.message);} finally{setSaving(false);} }} loading={saving}>Save alert config</Btn>
        </Card>
      )}

      {tab === "columns" && (
        <Card>
          <div style={{fontSize:13,color:"#6B7280",marginBottom:16}}>Choose which columns appear for this client. Applies to all users. Only admins can change this.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:10}}>Monthly analysis table</div>
              {Object.entries(DEFAULT_COLUMNS.analysis).map(([key,cfg]) => (
                <label key={key} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,marginBottom:8,cursor:cfg.locked?"default":"pointer"}}>
                  <input type="checkbox" disabled={cfg.locked}
                    checked={columns.analysis?.[key] ?? cfg.default}
                    onChange={e => setColumns(p => ({...p, analysis:{...(p.analysis||{}),[key]:e.target.checked}}))}/>
                  <span style={{color:cfg.locked?"#9CA3AF":"#374151"}}>{cfg.label}</span>
                  {cfg.locked && <span style={{fontSize:11,color:"#D1D5DB"}}>(always shown)</span>}
                </label>
              ))}
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:10}}>Dashboard KPI cards</div>
              {Object.entries(DEFAULT_COLUMNS.kpi).map(([key,cfg]) => (
                <label key={key} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,marginBottom:8,cursor:"pointer"}}>
                  <input type="checkbox"
                    checked={columns.kpi?.[key] ?? cfg.default}
                    onChange={e => setColumns(p => ({...p, kpi:{...(p.kpi||{}),[key]:e.target.checked}}))}/>
                  <span style={{color:"#374151"}}>{cfg.label}</span>
                </label>
              ))}
              <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:10,marginTop:16}}>Monthly trend table</div>
              {Object.entries(DEFAULT_COLUMNS.trend).map(([key,cfg]) => (
                <label key={key} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,marginBottom:8,cursor:"pointer"}}>
                  <input type="checkbox"
                    checked={columns.trend?.[key] ?? cfg.default}
                    onChange={e => setColumns(p => ({...p, trend:{...(p.trend||{}),[key]:e.target.checked}}))}/>
                  <span style={{color:"#374151"}}>{cfg.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div style={{marginTop:16,display:"flex",gap:10}}>
            <Btn onClick={() => setColumns(initColumns())} variant="secondary">Reset to defaults</Btn>
            <Btn onClick={async () => { setSaving(true); try { await api.admin.saveColumns(clientId, columns); ok("Column config saved"); } catch(e){setError(e.message);} finally{setSaving(false);} }} loading={saving}>Save column config</Btn>
          </div>
        </Card>
      )}

      {tab === "contract" && (
        <Card>
          <div style={{fontSize:13,color:"#6B7280",marginBottom:16}}>Contract dates used for renewal reminders and annual budget calculations.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Client display name (for reports)</label>
              <input style={inp} value={contract.display_name} onChange={e => setContract(p=>({...p,display_name:e.target.value}))} placeholder="e.g. SBI Securities"/></div>
            <div><label style={lbl}>Contract start date</label>
              <input type="date" style={inp} value={contract.start} onChange={e => setContract(p=>({...p,start:e.target.value}))}/></div>
            <div><label style={lbl}>Contract end date</label>
              <input type="date" style={inp} value={contract.end} onChange={e => setContract(p=>({...p,end:e.target.value}))}/>
              {contract.end && (() => {
                const days = Math.ceil((new Date(contract.end) - new Date()) / (1000*60*60*24));
                const color = days < 30 ? "#DC2626" : days < 60 ? "#D97706" : "#16A34A";
                return <div style={{fontSize:12,color,marginTop:4,fontWeight:600}}>
                  {days > 0 ? `${days} days remaining` : `Expired ${Math.abs(days)} days ago`}
                </div>;
              })()}
            </div>
          </div>
          <Btn onClick={async () => { setSaving(true); try { await api.admin.saveContract(clientId, contract); ok("Contract details saved"); } catch(e){setError(e.message);} finally{setSaving(false);} }} loading={saving}>Save contract details</Btn>
        </Card>
      )}
    </div>
  );
}
